// NIGHTSHIFT — Legacy palette re-pointed to canonical Design Library tokens
// (FLOWHUB_POS_DESIGN_LIBRARY.md §12 migration). Single-role legacy values are
// mapped to their canonical token here so the change propagates app-wide without
// touching every call site. Genuinely mixed-role values (middleRedPurple,
// mimiPink, cyclamen, mangoYellow, royal, salt, gradients) are LEFT AS-IS and
// handled per-usage / logged in NIGHTSHIFT_DECISION_LOG.md.
export const MAUI_PRIMARY = {
  //new, will be replaced with color token in design system:
  neonMoss: '#8CDF55',
  deepPurple: '#20026C',
  deepSwell: '#6126F5',
  dayDream: '#9488ff',
  sunburstLight: '#FFAC6A',
  sunburst: '#FF9341',
  sunburstLightSunburstGradient:
    'linear-gradient(270deg, #FFAC6A 0%, #FF9341 100%)', //sunset orange => mangoYellow
  alert: '#D04C00',
  pearl2: '#D5CCEF', // was #CCCEE8 (warm-ish gray) -> lavender border
  pearl4: '#9aa0b2', // was #8486A0 (gray) -> canonical muted

  mountainMeadow: '#1F8A5B', // was #2ADE93 (teal) -> success green
  mountainMeadowRGB: '31,138,91', // was 42,222,147 -> #1F8A5B
  // Success ink for small/medium label text on a white surface. The canonical
  // mountainMeadow is only 4.33:1 there, short of the 4.5:1 WCAG AA floor.
  mountainMeadowDark: '#137C50',
  darkPurple: '#20026C', // was #2E1679 -> primary purple
  purple40: '#6126F5', // was #5635BE -> violet accent
  sunsetOrange: '#FF9341', // was #FF8E4F -> commerce orange
  sunsetOrangeRGB: '255,147,65', // was 255,142,79 -> #FF9341
  mangoYellow: '#FFC04F', // KEPT yellow — legit yellow roles (mark text-highlight, Mantine yellow[3] scale, confetti); re-point would corrupt the yellow scale
  mangoYellowRGB: '255,192,79', // was '266,192,79' (invalid 266) -> correct RGB of #FFC04F
  orangeYellowGradient: 'linear-gradient(270deg, #FF8E4F 0%, #FFC04F 100%)', // HELD (gradient)
} as const

export const MAUI_SECONDARY = {
  purpleBlueGradient: 'linear-gradient(270deg, #A47AD1 0%, #286BDF 100%)', // HELD (gradient)
  ruby: '#D04C00', // was #F41E60 (pure red) -> destructive
  rubyRGB: '208,76,0', // was 244,30,96 -> #D04C00
  blue40: '#3066E7', // was #286BDF -> link/accent blue
  darkGray10: '#141414', // was #2D3540 -> primary text
  darkGray20: '#535353', // was #516074 -> secondary text
  lightGray40: '#D5CCEF', // was #CCD2DC -> lavender border
  lightGray60: '#F7F8FE', // was #F3F3F3 -> raised lavender surface
  royal: '#A47AD1', // HELD (gradient component)
  lavender: '#D5CCEF',
  salt: '#F0EBFF', // HELD (established filter-button surface)
  smoke: '#F7F8FE', // was #F1F7FF -> raised lavender surface
  white: '#FFFFFF',
} as const

export const OLD_REPLACE_ME = {
  blueGreenGradient: 'linear-gradient(270deg, #2ade93 0%, #44c1ea 100%)', // HELD (gradient)
  mimiPink: '#FCD9E9', // HELD (error/warning bg — no solid token; per-usage)
  mountainMeadow: '#1F8A5B', // was #2ade93 (teal) -> success
  middleRedPurple: '#141414', // was #110630 -> primary ink (dominant role: dark text/labels). Also recolors the legacy AppBar component's bg (near-black -> near-black, negligible); that component is only re-exported via the fh-ui barrel, not mounted in maui.
  hanBlue: '#3066E7', // was #3d74d5 -> link/accent blue
  ruby: '#D04C00', // was #f41e60 -> destructive
  taupeGray: '#9aa0b2', // was #8f8f8f -> canonical muted
  isabelline: '#ECEAF5', // was #eaeaea (warm gray) -> default lavender border
  graniteGray: '#535353', // was #665f79 -> secondary text
  pastelBlue: '#9aa0b2', // was #aeb7c5 -> canonical muted
  azureishWhite: '#E6E3F2', // was #dae1ea -> lavender border
  whiteSmoke: '#F7F8FE', // was #f4f7fb -> raised lavender surface
  ghostWhite: '#F7F8FE', // was #f9fbfe -> raised lavender surface
  lightSeaGreen: '#1F8A5B', // was #1ea99d (teal) -> success
  duckEggGreen: '#EAF7EF', // was #eefcf6 -> pale green surface
  mountainMeadowLight: '#1F8A5B', // was #54e4a8 (mint) -> success
  mantis: '#1F8A5B', // was #74d861 -> success
  crayola: '#1F8A5B', // was #20b474 -> success
  pearlAqua: '#1F8A5B', // was #7eeabd (mint) -> success [watch: review if used as a surface]
  aeroBlue: '#EAF7EF', // was #c7f6e1 -> pale green surface
  mustard: '#FF9341', // was #ffe652 -> commerce orange
  gargoyleGas: '#FF9341', // was #ffda40 -> commerce orange
  pictonBlue: '#3066E7', // was #44c1ea (cyan) -> link/accent blue
  mediumSkyBlue: '#3066E7', // was #8adeec -> link/accent blue
  hanBlueDark: '#20026C', // was #0c3988 -> primary purple
  hanBlueLight: '#3066E7', // was #4f8edb -> link/accent blue
  spanishViolet: '#20026C', // was #47257d -> primary purple
  iris: '#6126F5', // was #5d2dc5 -> violet accent
  mediumPurple: '#6126F5', // was #8f71f4 -> violet accent
  cyclamen: '#D04C00', // was #ed77a2 -> destructive (dominant role: error / negative-trend / limit-warning)
  cyclamenRGB: '208, 76, 0', // was 237,119,162 -> #D04C00
  white: '#ffffff',
  paleGrey: '#F7F8FE', // was #f4f7fb -> raised lavender surface
  paleGreyTwo: '#F7F8FE', // was #f1f4f7 -> raised lavender surface
  paleGreyThree: '#E6E3F2', // was #dae1ea -> lavender border
} as const

export const BrandColors = {
  ...OLD_REPLACE_ME,
  ...MAUI_PRIMARY,
  ...MAUI_SECONDARY,
} as const

export type BrandColor = keyof typeof BrandColors
